/**
 * A bounded ring buffer of recent browser errors, installed before app code.
 * Ported from Prism (itself lifted from Links and CMS).
 * (byte-identical between the two repos).
 *
 * Two hard rules, both learned from the fact that this runs first in five
 * production apps:
 *
 *  1. It can never throw. Anything that throws here breaks the app it was
 *     added to observe, on boot, before any of that app's own error handling
 *     exists. Every hook body is wrapped.
 *  2. It never swallows. The original console.error still fires and the
 *     listeners are passive, so existing behaviour and any other error
 *     reporter carry on untouched.
 */
import type { ConsoleEntry } from './redact';

const MAX = 25;
const MAX_MESSAGE = 2000;

let buffer: ConsoleEntry[] = [];
let installed = false;

function push(kind: ConsoleEntry['kind'], message: string): void {
  try {
    if (!message) return;
    buffer.push({
      at: new Date().toISOString(),
      kind,
      message: message.slice(0, MAX_MESSAGE),
    });
    if (buffer.length > MAX) buffer = buffer.slice(-MAX);
  } catch {
    /* never throw from an error hook */
  }
}

function stringify(value: unknown): string {
  try {
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    if (typeof value === 'string') return value;
    return JSON.stringify(value) ?? String(value);
  } catch {
    return '[unserializable]';
  }
}

/** Idempotent. Call as early as possible - ideally the first import in the entry module. */
export function installConsoleCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  try {
    window.addEventListener('error', (e) => {
      push('error', e?.message ? `${e.message}${e.filename ? ` (${e.filename}:${e.lineno})` : ''}` : 'error');
    });
  } catch {
    /* ignore */
  }

  try {
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      push('unhandledrejection', stringify(e?.reason));
    });
  } catch {
    /* ignore */
  }

  try {
    const original = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      push('console', args.map(stringify).join(' '));
      original(...args); // never swallow
    };
  } catch {
    /* ignore */
  }
}

/** Snapshot, oldest first. Not redacted - redact at submit time. */
export function getConsoleErrors(): ConsoleEntry[] {
  return buffer.slice();
}

export function clearConsoleErrors(): void {
  buffer = [];
}

/** Test seam. */
export function __resetForTests(): void {
  buffer = [];
  installed = false;
}
